<script setup>
import debounce from "lodash.debounce";
import ApiService from "~/services/ApiService";

const isSearching = ref(false);
const isLoading = ref(false);
const isPaginationDisabled = ref(false);
const error = ref({});
const currentPage = ref(1);
const games = ref([]);
const totalPages = ref(0);
const showingItems = ref(false);
const totalItems = ref(0);
const searchString = ref("");

const $emmit = defineEmits(["isSearching"]);

const searchGames = debounce(() => {
  error.value = {};
  isSearching.value = searchString.value.length !== 0;

  console.log("Getting games", searchString.value.length);

  if (searchString.value.length > 2) {
    try {
      isLoading.value = true;
      let getGamesUrl = `/api/casino-games?page=${currentPage.value}&limit=12`;

      if (searchString.value && searchString.value !== "") {
        getGamesUrl += `&search=${searchString.value}`;
      }

      getGamesUrl += `&order=clicks`;

      ApiService.get(getGamesUrl)
        .then((res) => {
          if (currentPage.value > 1) {
            games.value.push(...res.data.data);
          } else {
            games.value = res.data.data;
          }

          if (games.value.length === 0) {
            error.value = {
              message: "Não encontramos resultados para esta busca",
              type: "warning",
            };
          }

          currentPage.value = res.data.meta.current_page;
          totalPages.value = res.data.meta.last_page;
          showingItems.value = res.data.meta.to;
          totalItems.value = res.data.meta.total;

          isLoading.value = false;
        })
        .catch((error) => {
          console.log(error);
        });
    } catch (error) {
      console.log("We were unable to fetch the casino games.");
      console.log(error);
      isLoading.value = false;
    }
  } else {
    games.value = [];
    error.value = {
      message: "Digite no mínimo 3 caracteres para pesquisar",
      type: "warning",
    };
  }
}, 500);

const gameCountPercentage = computed(() => {
  return Math.round((showingItems.value / totalItems.value) * 100);
});

const showPagination = computed(() => {
  if (
    games.value.length > 0 ||
    (!isLoading.value &&
      searchString.value.length > 2 &&
      games.value.length > 0)
  ) {
    return true;
  } else {
    return false;
  }
});

async function nextPage() {
  if (currentPage.value !== totalPages.value) {
    currentPage.value++;
    await searchGames();
  }
}

function clearSearch() {
  searchString.value = "";
  isSearching.value = false;
}

watch(currentPage, () => {
  if (currentPage.value === totalPages.value) {
    isPaginationDisabled.value = true;
  } else {
    isPaginationDisabled.value = false;
  }
});

watch(games, () => {
  if (currentPage.value === totalPages.value) {
    isPaginationDisabled.value = true;
  } else {
    isPaginationDisabled.value = false;
  }
});

watch(searchString, () => {
  games.value = [];
  currentPage.value = 1;
  totalPages.value = 0;
  totalItems.value = 0;
  isPaginationDisabled.value = false;
});

watch(isSearching, () => {
  $emmit("isSearching", isSearching.value);
});
</script>

<template>
  <div
    class="fixed left-0 top-0 z-[900] h-dvh w-screen bg-black/70"
    v-if="isSearching"
    id="overlay"
    @click="clearSearch"
  ></div>

  <div class="relative" :class="{ 'z-[1000]': isSearching }">
    <div class="flex w-full gap-2" @click.stop="">
      <form
        autocomplete="off"
        class="relative w-full overflow-hidden rounded-md border border-brand-separator bg-brand-background-brighten py-1 lg:py-2"
        @submit.prevent=""
      >
        <Icon
          name="material-symbols:search"
          v-if="!isSearching"
          class="absolute right-3 top-2 text-lg lg:top-3 lg:text-xl"
        />
        <Icon
          name="fa6-solid:delete-left"
          v-if="isSearching"
          @click="clearSearch"
          class="absolute right-3 top-2 text-lg lg:top-3 lg:text-xl"
        />
        <input
          @input="searchGames"
          name="search-games-provider"
          class="h-full w-full bg-transparent px-3 outline-none placeholder:text-bzMenuTextDark"
          type="text"
          placeholder="Pesquise um jogo..."
          v-model="searchString"
        />
      </form>
    </div>

    <div
      class="absolute z-20 mt-4 w-full rounded-md bg-brand-background"
      v-if="isSearching"
    >
      <div
        class="scrollbar-hide relative flex max-h-[70vh] flex-col justify-between lg:max-h-[40vh]"
      >
        <div class="scrollbar-hide overflow-auto p-4">
          <div
            class="grid min-h-0 flex-1 grid-cols-2 gap-4 xs:grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6"
          >

            <BaseGame
              v-if="!isLoading"
              :data="game"
              v-for="game in games"
              :key="game.id"
            />

            <template
              v-for="item in 6 - (games.length < 6 ? games.length : 0)"
              v-if="isLoading"
            >
              <div
                class="relative aspect-[3/4] w-full animate-pulse cursor-pointer overflow-hidden rounded-xl bg-secondary"
              ></div>
            </template>
          </div>

          <template v-if="games.length === 0">
            <BaseAlert
              class="text-sm"
              v-if="error"
              :message="error.message"
              :type="error.type"
            />
          </template>
        </div>

        <!-- pagination -->
        <div
          class="relative flex w-full justify-center rounded-md bg-brand-background from-transparent to-brand-background px-4 py-8 before:absolute before:-top-[45px] before:left-0 before:h-[50px] before:w-full before:bg-gradient-to-b before:content-['']"
          v-if="showPagination"
        >
          <div>
            <div class="h-0.5 w-44 bg-brand-separator">
              <div
                :style="`width: ${gameCountPercentage}%;`"
                class="h-full bg-brand-primary transition-all duration-500"
              ></div>
            </div>
            <div class="py-3 text-center text-xs font-bold">
              Mostrando {{ showingItems }} de {{ totalItems }}
            </div>
            <div class="flex justify-center">
              <BaseButton
                v-if="!isPaginationDisabled"
                :disabled="isPaginationDisabled"
                size="sm"
                variant="secondary"
                @click="nextPage()"
              >
                <span v-if="!isLoading">Ver mais</span>
                <Icon
                  name="fa6-solid:circle-notch"
                  class="animate-spin"
                  v-else
                />
              </BaseButton>
            </div>
          </div>
        </div>
        <!-- /pagination -->
      </div>
    </div>
  </div>
</template>
